import React from "react";
import { Icon } from "./primitives.jsx";

/* Article reading overlay (modal). `go(pageId)` navigates real URLs. */
export function ArticleReader({ article, labels, onClose, go }) {
  const closeRef = React.useRef(null);

  React.useEffect(() => {
    if (!article) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [article, onClose]);

  if (!article) return null;
  const body = article.body || [article.excerpt];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={article.title}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "clamp(16px, 5vh, 64px) var(--gutter)",
        background: "rgba(17,17,19,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        overflowY: "auto"
      }}
    >
      <article
        className="rw-modal-panel"
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", width: "100%", maxWidth: 680, background: "var(--surface-card)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-lg)", overflow: "hidden", fontFamily: "var(--font-article)" }}
      >
        <div style={{ position: "relative", height: 132, background: "var(--brand-ink)", overflow: "hidden" }}>
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, opacity: 0.5, background: "repeating-linear-gradient(135deg, transparent 0 16px, rgba(255,255,255,0.05) 16px 17px)" }} />
          <span style={{ position: "absolute", left: 24, top: 24, fontSize: "var(--text-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fff", background: "var(--brand-red)", padding: "5px 11px", borderRadius: "var(--radius-pill)" }}>{article.tag}</span>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label={labels.close}
            style={{ position: "absolute", right: 16, top: 16, width: 40, height: 40, borderRadius: "var(--radius-sm)", border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.24)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.14)")}
          >
            <Icon name="x" size={20} color="#fff" />
          </button>
        </div>

        <div style={{ padding: "clamp(1.5rem, 4vw, var(--space-8))" }}>
          <div style={{ display: "flex", gap: 10, fontSize: "var(--text-xs)", color: "var(--text-muted)", marginBottom: 14 }}>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.read}</span>
          </div>
          <h1 style={{ margin: 0, fontFamily: "var(--font-article)", fontWeight: 800, fontSize: "clamp(1.7rem, 3.6vw, var(--text-2xl))", lineHeight: 1.12, letterSpacing: "var(--tracking-tight)", color: "var(--brand-ink)", textWrap: "balance" }}>{article.title}</h1>
          <div style={{ marginTop: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            {body.map((p, i) => (
              <p key={i} style={{ margin: 0, fontSize: "var(--text-md)", lineHeight: 1.7, color: "var(--text-body)", textWrap: "pretty" }}>{p}</p>
            ))}
          </div>

          <div style={{ marginTop: "var(--space-7)", paddingTop: "var(--space-5)", borderTop: "1px solid var(--border-subtle)", display: "flex", flexWrap: "wrap", gap: "var(--space-4)", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", maxWidth: 360 }}>{labels.readerNote}</span>
            <button
              onClick={() => { onClose(); go("contact"); }}
              style={{ flexShrink: 0, background: "var(--brand-red)", color: "#fff", border: "none", cursor: "pointer", padding: "0 22px", height: "var(--control-h-md)", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-article)", fontWeight: 700, fontSize: "var(--text-sm)", display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "var(--shadow-brand)" }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.06)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "none")}
            >
              {labels.talk} <Icon name="arrow-right" size={16} color="#fff" />
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
